// Plan 15-07 Task 2 — QRCanvas (DEC-002 — encode-side of the add-a-friend triad).
//
// Renders a 200×200 canvas-rendered QR code using `qrcode-generator@2.0.4`
// (the encode-only library installed in Plan 15-05; ~2KB gzipped). Modules
// are drawn at `--color-inkwell` on a white background so the contrast
// stays above WCAG AA on the take-two palette.
//
// Threat model — T-15-31: this component MUST be passed the player's OWN
// share URL only. Callers (ShareMyCodeSheet) read the player's friend_code
// from `$authBearer` / profile state and never echo arbitrary user input.

import qrcode from 'qrcode-generator'
import { type JSX, useEffect, useRef } from 'react'
import { actionTokens, themeTokens } from '../../styles/tokens'

// Folk-art reskin: the semantic flip turned actionTokens.textPrimary DARK, so
// the QR quiet-zone background must come from a light surface token instead —
// otherwise the inkwell-dark modules render dark-on-dark (unscannable). The
// take-two palette is static (single canonical theme), so this resolves the
// near-white card surface.
const QR_BACKGROUND = themeTokens.surfaceLight

export interface QRCanvasProps {
  readonly shareUrl: string
  readonly size?: number
  /** Accessible label override; defaults to the share URL. */
  readonly ariaLabel?: string
}

const DEFAULT_SIZE = 200

export function QRCanvas({
  shareUrl,
  size = DEFAULT_SIZE,
  ariaLabel,
}: QRCanvasProps): JSX.Element {
  const ref = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    // Encode first — happens regardless of canvas backend (so unit tests
    // in happy-dom can still assert encoder invocation even though the 2D
    // context isn't available there).
    // type-number=0 → auto, ECC level M (Medium) per 15-PATTERNS.md.
    const qr = qrcode(0, 'M')
    qr.addData(shareUrl)
    qr.make()
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const moduleCount = qr.getModuleCount()
    // Cell size — at least 1px. Round down so the encoded matrix fits.
    const cell = Math.max(1, Math.floor(size / moduleCount))
    // Light background — WCAG-safe contrast for the inkwell-dark modules.
    ctx.fillStyle = QR_BACKGROUND
    ctx.fillRect(0, 0, size, size)
    // Module color — canonical inkwell from tokens. CSS-vars are not
    // accessible from a 2D canvas context, so we read from the shared
    // token constant instead.
    ctx.fillStyle = actionTokens.inkwell
    for (let r = 0; r < moduleCount; r++) {
      for (let c = 0; c < moduleCount; c++) {
        if (qr.isDark(r, c)) {
          ctx.fillRect(c * cell, r * cell, cell, cell)
        }
      }
    }
  }, [shareUrl, size])

  return (
    <canvas
      ref={ref}
      data-testid="qr-canvas"
      width={size}
      height={size}
      role="img"
      aria-label={ariaLabel ?? shareUrl}
      style={{
        display: 'block',
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: QR_BACKGROUND,
        borderRadius: '8px',
      }}
    />
  )
}
